import { inject, Injectable } from '@angular/core';
import {
	deleteObject,
	FullMetadata,
	getDownloadURL,
	getMetadata,
	listAll,
	ListResult,
	ref,
	Storage,
	StringFormat,
	uploadBytes,
	UploadResult,
	uploadString
} from '@angular/fire/storage';
import { UuidService } from '@nova-portal/frontend-lib';
import { FirebaseError } from 'firebase/app';
import { AngularFireContextService } from './angular-fire-context.service';

@Injectable()
export class ApiFireStorageClient {
	private readonly storage = inject(Storage);
	private readonly uuidService = inject(UuidService);
	private readonly context = inject(AngularFireContextService);

	public generateUniqueFilename(extension: string): string {
		return `${this.uuidService.generate()}.${extension}`;
	}

	public upload(path: string, dataUrl: string): Promise<UploadResult> {
		return this.context.run(() =>
			uploadString(ref(this.getStorage(), this.extractPath(path)), dataUrl, StringFormat.DATA_URL)
		);
	}

	public uploadBlob(path: string, file: Blob): Promise<UploadResult> {
		return this.context.run(() => uploadBytes(ref(this.getStorage(), this.extractPath(path)), file));
	}

	public getMetadata(path: string): Promise<FullMetadata | null> {
		return path
			? this.context.run(() => getMetadata(ref(this.getStorage(), this.extractPath(path))))
			: Promise.resolve(null);
	}

	public getUrl(path: string): Promise<string | null> {
		return path
			? this.context.run(() => getDownloadURL(ref(this.getStorage(), this.extractPath(path))))
			: Promise.resolve(null);
	}

	public exists(path: string): Promise<boolean> {
		if (!path) {
			return Promise.resolve(false);
		}

		const index = path.lastIndexOf('/');
		const file = path.substring(index + 1);
		path = index > 0 ? this.extractPath(path).substring(0, index) : '';
		return this.list(path).then(files => !!files?.items?.find(f => f?.name === file));
	}

	public list(path: string): Promise<ListResult | null> {
		return path ? this.context.run(() => listAll(ref(this.getStorage(), path))) : Promise.resolve(null);
	}

	public delete(path: string): Promise<boolean> {
		return path
			? this.context
					.run(() => deleteObject(ref(this.getStorage(), this.extractPath(path))))
					.then(() => true)
					.catch((error: FirebaseError) => {
						if (error.code === 'storage/object-not-found') {
							return false;
						}
						throw error;
					})
			: Promise.resolve(false);
	}

	private extractPath(path: string): string {
		return path.startsWith('gs://') ? path.substring(path.indexOf('/', 5)) : path;
	}

	protected getStorage(): Storage {
		return this.storage;
	}
}
